import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Send, Search, ChartLine, Target } from 'lucide-react';

const SalesLeadsPro = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/#contact');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div style={{ background: '#FAFAFA', minHeight: '100vh' }} className="text-[#0D2C22]">
      {/* Hero Section */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 96px', display: 'grid', gap: '40px', alignItems: 'center' }} 
             className="lg:grid-cols-12">
          
          {/* Left Column - Content */}
          <div style={{ gridColumn: 'span 6 / span 6', position: 'relative', zIndex: 10 }} className="lg:col-span-6">
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                marginBottom: '24px',
                background: 'white',
                border: '1px solid rgba(13, 44, 34, 0.1)',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 44, 34, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <ArrowLeft size={14} />
              Back to Logardt Group
            </button>

            {/* Tag */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: 'white',
              border: '1px solid rgba(13, 44, 34, 0.1)',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '24px'
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E5A93D' }}></span>
              for growing businesses
            </div>

            {/* Main Headline */}
            <h1 className="font-display" style={{
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontWeight: '700',
              lineHeight: '0.95',
              marginTop: '24px',
              letterSpacing: '-0.02em'
            }}>
              Fill your <span style={{ fontStyle: 'italic', color: 'rgba(13, 44, 34, 0.8)' }}>pipeline</span>.<br />
              Not your <span style={{ textDecoration: 'underline', textDecorationColor: '#E5A93D', textDecorationThickness: '4px', textUnderlineOffset: '4px' }}>inbox</span>.
            </h1>

            {/* Description */}
            <p style={{
              marginTop: '24px',
              fontSize: '18px',
              color: 'rgba(13, 44, 34, 0.75)',
              maxWidth: '560px',
              lineHeight: '1.6'
            }}>
              An AI-powered lead generation & email automation engine, purpose-built for B2B sales teams. Discover prospects, personalise outreach, send via SMTP, and outrank on Google & Bing.
            </p>

            {/* CTA Buttons */}
            <div style={{ marginTop: '32px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={scrollToContact}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px 24px',
                  background: '#0D2C22',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(13, 44, 34, 0.15)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 44, 34, 0.9)'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#0D2C22'}
              >
                Get access
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>

              <button
                onClick={scrollToContact}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px 24px',
                  background: 'transparent',
                  color: '#0D2C22',
                  border: '1px solid rgba(13, 44, 34, 0.2)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 44, 34, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                Request a demo
              </button>
            </div>

            {/* Tech Stack Tags */}
            <div style={{
              marginTop: '40px',
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              fontSize: '11px',
              color: 'rgba(13, 44, 34, 0.6)',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              flexWrap: 'wrap'
            }}>
              <span>AI-Powered</span>
              <span>•</span>
              <span>SMTP Integration</span>
              <span>•</span>
              <span>Bing + Google SEO</span>
            </div>
          </div>

          {/* Right Column - Demo Card */}
          <div style={{ gridColumn: 'span 6 / span 6', position: 'relative' }} className="lg:col-span-6">
            <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden' }}>
              <img
                alt="Sales dashboard"
                style={{ width: '100%', height: '520px', objectFit: 'cover' }}
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkYXNoYm9hcmQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzg1MjQ4NDU2fDA&ixlib=rb-4.1.0&q=85"
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(13, 44, 34, 0.6), transparent, transparent)'
              }}></div>
              
              {/* Live Pipeline Card */}
              <div style={{
                position: 'absolute',
                bottom: '24px',
                left: '24px',
                right: '24px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 8px 32px rgba(13, 44, 34, 0.12)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(13, 44, 34, 0.6)' }}>
                      Live pipeline · demo
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#10b981' }}>
                      + 24 today
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', textAlign: 'center' }}>
                    {[
                      { label: 'Discovered', value: '128' },
                      { label: 'Contacted', value: '72' },
                      { label: 'Replied', value: '19' },
                      { label: 'Booked', value: '6' }
                    ].map((stat, idx) => (
                      <div key={idx} style={{
                        padding: '8px',
                        background: '#F7F5F0',
                        border: '1px solid rgba(13, 44, 34, 0.05)',
                        borderRadius: '8px'
                      }}>
                        <div className="font-display" style={{ fontSize: '20px', fontWeight: '700' }}>{stat.value}</div>
                        <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(13, 44, 34, 0.6)' }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px 96px' }}>
        <div style={{ marginBottom: '48px', maxWidth: '800px' }}>
          <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(13, 44, 34, 0.6)' }}>
            a growth stack
          </div>
          <h2 className="font-display" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '700', marginTop: '8px', lineHeight: '1.1' }}>
            Built for the way sales actually scale.
          </h2>
        </div>

        <div style={{ display: 'grid', gap: '16px' }} className="md:grid-cols-6">
          {/* Large Feature Card */}
          <div style={{
            gridColumn: 'span 3',
            gridRow: 'span 2',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: '#0D2C22',
            color: 'white'
          }} className="md:col-span-3 md:row-span-2">
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              marginBottom: '16px',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#E5A93D'
            }}>
              <Sparkles size={20} />
            </div>
            <div className="font-display" style={{ fontSize: '20px', fontWeight: '700' }}>AI Prospecting</div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
              Describe your ideal customer profile — AI returns qualified leads with fit scores, pain-points and outreach hooks.
            </div>
            <img
              alt="AI prospecting"
              style={{ marginTop: '24px', borderRadius: '8px', height: '208px', width: '100%', objectFit: 'cover', opacity: 0.9 }}
              src="https://images.unsplash.com/photo-1509600110300-21b9d5fedeb7?w=600"
            />
          </div>

          {/* Small Feature Cards */}
          <div style={{
            gridColumn: 'span 3',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(13, 44, 34, 0.1)',
            background: 'white'
          }} className="md:col-span-3">
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              marginBottom: '16px',
              background: '#F7F5F0',
              color: '#0D2C22'
            }}>
              <Send size={20} />
            </div>
            <div className="font-display" style={{ fontSize: '20px', fontWeight: '700' }}>Email Automation</div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(13, 44, 34, 0.7)' }}>
              Send hyper-personalised outreach 1:1 or in campaigns with merge tags via your SMTP server.
            </div>
          </div>

          <div style={{
            gridColumn: 'span 2',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(13, 44, 34, 0.1)',
            background: 'white'
          }} className="md:col-span-2">
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              marginBottom: '16px',
              background: '#F7F5F0',
              color: '#0D2C22'
            }}>
              <Search size={20} />
            </div>
            <div className="font-display" style={{ fontSize: '20px', fontWeight: '700' }}>SEO for Google & Bing</div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(13, 44, 34, 0.7)' }}>
              Keyword research, meta tags, and content briefs tuned for both engines.
            </div>
          </div>

          <div style={{
            gridColumn: 'span 4',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(13, 44, 34, 0.1)',
            background: 'white'
          }} className="md:col-span-4">
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              marginBottom: '16px',
              background: '#F7F5F0',
              color: '#0D2C22'
            }}>
              <ChartLine size={20} />
            </div>
            <div className="font-display" style={{ fontSize: '20px', fontWeight: '700' }}>Pipeline Analytics</div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(13, 44, 34, 0.7)' }}>
              Track leads, contacted, replied, booked. See weekly trends and campaign performance in one glance.
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how" style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px 96px' }}>
        <div style={{ display: 'grid', gap: '32px' }} className="md:grid-cols-3">
          {[
            { num: '01', title: 'Define your ICP', desc: 'Industry, company size, location. That\'s it.' },
            { num: '02', title: 'Discover & score', desc: 'AI surfaces prospects with fit scores + pain-point signals.' },
            { num: '03', title: 'Draft & send', desc: 'Personalised emails via SMTP. Track opens & replies.' }
          ].map((step, idx) => (
            <div key={idx} style={{ borderTop: '2px solid rgba(13, 44, 34, 0.2)', paddingTop: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#E5A93D' }}>{step.num}</div>
              <div className="font-display" style={{ fontSize: '24px', fontWeight: '700', marginTop: '8px' }}>{step.title}</div>
              <div style={{ color: 'rgba(13, 44, 34, 0.7)', marginTop: '8px' }}>{step.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section - Added for better text-to-code ratio */}
      <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px 96px', background: '#F7F5F0', marginBottom: '96px', borderRadius: '16px', padding: '80px 60px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="font-display" style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '700', marginBottom: '24px', textAlign: 'center' }}>
            Why Sales Teams Choose Sales Leads Pro
          </h2>
          <p style={{ fontSize: '18px', color: 'rgba(13, 44, 34, 0.7)', lineHeight: '1.8', marginBottom: '48px', textAlign: 'center' }}>
            Built specifically for B2B sales professionals who need to scale outreach without sacrificing personalization. Our AI-powered platform combines intelligent prospecting with automated email campaigns to help you fill your pipeline with qualified leads consistently.
          </p>
          
          <div style={{ display: 'grid', gap: '32px', marginTop: '48px' }}>
            <div>
              <h3 className="font-display" style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#0D2C22' }}>
                Save 20+ Hours Per Week on Prospecting
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(13, 44, 34, 0.75)', lineHeight: '1.7' }}>
                Stop manually searching for leads on LinkedIn and company databases. Our AI prospecting engine analyzes millions of data points to identify prospects that match your ideal customer profile, complete with company information, decision-maker details, and personalized outreach hooks. What used to take days now happens in minutes.
              </p>
            </div>
            
            <div>
              <h3 className="font-display" style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#0D2C22' }}>
                Personalized Outreach at Scale
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(13, 44, 34, 0.75)', lineHeight: '1.7' }}>
                Generic email templates get ignored. Our AI generates personalized email copy for each prospect based on their company, role, and pain points. Send hundreds of emails that feel one-to-one, with dynamic merge tags for names, companies, industries, and custom variables. Your prospects won't know it's automated because each message is uniquely tailored to them.
              </p>
            </div>
            
            <div>
              <h3 className="font-display" style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#0D2C22' }}>
                Optimize for Both Google and Bing
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(13, 44, 34, 0.75)', lineHeight: '1.7' }}>
                Most B2B buyers research vendors before engaging with sales. Our integrated SEO engine helps your content rank on both Google and Bing (which owns 20-30% of desktop search in key B2B markets). Generate keyword-optimized content briefs, meta descriptions, and schema markup that attracts inbound leads while your outbound campaigns run.
              </p>
            </div>
            
            <div>
              <h3 className="font-display" style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#0D2C22' }}>
                Real-Time Pipeline Visibility
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(13, 44, 34, 0.75)', lineHeight: '1.7' }}>
                Track every lead from discovery to booking in one unified dashboard. See who opened your emails, which prospects replied, and which conversations are moving forward. Weekly trend reports show you what's working so you can double down on winning strategies. No more spreadsheets or guessing—just clear metrics that drive results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Section */}
      <section id="seo" style={{ background: '#0D2C22', color: 'white', padding: '96px 24px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gap: '48px', alignItems: 'center' }} className="md:grid-cols-2">
          <div>
            <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#E5A93D' }}>
              SEO engine
            </div>
            <h2 className="font-display" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '700', marginTop: '12px' }}>
              Rank on Google.<br />And on <span style={{ textDecoration: 'underline', textDecorationColor: '#E5A93D' }}>Bing</span>.
            </h2>
            <p style={{ marginTop: '16px', color: 'rgba(255, 255, 255, 0.7)', maxWidth: '560px' }}>
              Bing owns 20-30% of desktop search in key B2B markets. Our engine returns tactics tuned for both — exact-match weighting for Bing, helpful-content signals for Google.
            </p>
            <ul style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
              {[
                'Keyword clusters with intent + difficulty',
                'Title & meta description generator',
                'JSON-LD schema snippets',
                'Content briefs with E-E-A-T signals'
              ].map((item, idx) => (
                <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <Target size={16} style={{ marginTop: '4px', color: '#E5A93D' }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              { label: 'title', value: 'How to Scale Your B2B Sales Pipeline | 2026 Guide' },
              { label: 'desc', value: 'Complete 2026 playbook to rank your B2B sales on Google & Bing. Includes schema, meta and content clusters.' },
              { label: 'url', value: 'sales-leads-pro.com › guides › scale-b2b-pipeline' }
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '16px',
                backdropFilter: 'blur(8px)'
              }}>
                <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h3 className="font-display" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '700' }}>
          Ready to fill your pipeline?
        </h3>
        <p style={{ marginTop: '16px', color: 'rgba(13, 44, 34, 0.7)', maxWidth: '640px', margin: '16px auto 0' }}>
          Get started with Sales Leads Pro. Contact us for a personalized demo and quote.
        </p>
        <button
          onClick={scrollToContact}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '32px',
            padding: '16px 32px',
            background: '#0D2C22',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(13, 44, 34, 0.15)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 44, 34, 0.9)'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#0D2C22'}
        >
          Get started
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="m12 5 7 7-7 7"></path>
          </svg>
        </button>
      </section>
    </div>
  );
};

export default SalesLeadsPro;
